import { useEffect } from 'react';
import { getGASessionId, getGAClientId } from '../utils/analytics';

interface UseKeyboardHandlersProps {
  isOpen: boolean;
  feedbackText: string;
  isSubmitting: boolean;
  isSubmitted: boolean;
  handleClose: () => void;
  handleSubmit: (e: React.FormEvent) => void;
}

export const useKeyboardHandlers = ({
  isOpen,
  feedbackText,
  isSubmitting,
  isSubmitted,
  handleClose,
  handleSubmit
}: UseKeyboardHandlersProps) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Escape to close
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (feedbackText.trim() && !isSubmitting && !isSubmitted) {
          handleSubmit(e as any);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, feedbackText, isSubmitting, isSubmitted, handleClose, handleSubmit]);
};

interface UseFocusAndGAProps {
  isOpen: boolean;
  isSubmitted: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  setGaSessionId: (id: string | null) => void;
  setGaClientId: (id: string | null) => void;
}

export const useFocusAndGA = ({
  isOpen,
  isSubmitted,
  textareaRef,
  setGaSessionId,
  setGaClientId
}: UseFocusAndGAProps) => {
  // Focus management and load GA values
  useEffect(() => {
    if (isOpen && textareaRef.current && !isSubmitted) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }

    // Load GA tracking values when modal opens
    if (isOpen) {
      getGASessionId().then(setGaSessionId);
      getGAClientId().then(setGaClientId);
    }
  }, [isOpen, isSubmitted, textareaRef, setGaSessionId, setGaClientId]);
};

interface UseDropdownClickOutsideProps {
  isDropdownOpen: boolean;
  modalRef: React.RefObject<HTMLDivElement>;
  setIsDropdownOpen: (open: boolean) => void;
}

export const useDropdownClickOutside = ({
  isDropdownOpen,
  modalRef,
  setIsDropdownOpen
}: UseDropdownClickOutsideProps) => {
  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isDropdownOpen && modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, modalRef, setIsDropdownOpen]);
};